import * as React from 'react';
import { Panel } from './panel';
import { bind } from 'bind-decorator';
import { MarkSceneGraphDirty, SetOwningOverlayKey } from './sgproto';

declare global
{
	namespace JSX
	{
		interface IntrinsicElements
		{
			"vsg-app": any;
		}
	}
}

interface SGAppProps
{
	children?: React.ReactNode;
	owning_overlay_key?: string;
	web_secret?: string;
}

interface SGAppState
{
	bFontsLoaded: boolean;
	nForcedUpdateNumber: number;
	bShowDebugPointer: boolean;
}

export class SGApp extends React.Component<SGAppProps, SGAppState>
{
	private static s_Current: SGApp = null;
	private static readonly k_EmbeddedDataRows = 1;

	public m_DomRef: React.RefObject<any> = null;
	public m_scalingDomRef: React.RefObject<any> = null;
	public m_CanvasRef: React.RefObject<HTMLCanvasElement> = null;
	public m_DebugPointerRef: React.RefObject<HTMLDivElement> = null;
	public m_Observer: MutationObserver = null;
	public m_nEmbeddedDataWidth: number = 0;
	public m_nPixelX: number = 0;
	public m_CanvasContext: CanvasRenderingContext2D = null;
	public m_Pixels: ImageData = null;
	public m_mapPanels: Map<number, Panel> = new Map();
	public m_rAvailableEmbeddedIndicesQueue: number[] = []; // Queue for which indices are available to store embedded data in. Oldest free indices at the front.
	public m_rEmbeddedIndicesToClear: number[] = [];
	public m_nDirtyXMin: number = -1;
	public m_nDirtyXMax: number = -1;
	public m_fCurrentScale = 1.0;

	public static get IsSceneGraphApp(): boolean
	{
		return ( SGApp.Current() !== null );
	}

	public static Current()
	{
		return SGApp.s_Current;
	}

	constructor( props: any )
	{
		super( props );
		SGApp.s_Current = this;
		this.state = {
			bFontsLoaded: false,
			nForcedUpdateNumber: 0,
			bShowDebugPointer: false,
		};

		this.m_DomRef = React.createRef();
		this.m_scalingDomRef = React.createRef();
		this.m_CanvasRef = React.createRef();
		this.m_DebugPointerRef = React.createRef();
	}

	@bind public toggleDebugPointer()
	{
		this.setState( { bShowDebugPointer: !this.state.bShowDebugPointer } );
	}

	@bind private onMouseMove( ev: MouseEvent )
	{
		if ( !this.state.bShowDebugPointer || !this.m_DebugPointerRef.current )
		{
			return;
		}

		this.m_DebugPointerRef.current.style.transform = "translateX( " + ev.clientX + "px ) translateY( " + ev.clientY + "px )";
	}

	// Force UV bounds for all Panels to be updated after page reflows.
	@bind public forceLayoutUpdate()
	{
		Panel.s_bPanelsAreDirty = true;
		this.setState( { nForcedUpdateNumber: this.state.nForcedUpdateNumber + 1 } );
	}

	public componentDidMount()
	{
		let actualDocument = this.m_DomRef.current.ownerDocument;
		let actualWindow = actualDocument.defaultView;

		actualDocument.body.classList.add( 'SGApp' );
		document.documentElement.classList.add( 'VROverlay' );

		// Number of panel UVs we can have in the embedded pixel array. Three embedded pixels per index, one magic pixel at the front.
		this.m_nEmbeddedDataWidth = actualWindow.innerWidth;
		let nAvailableIndices = Math.max( 0, Math.floor( ( this.m_nEmbeddedDataWidth - 1 ) / 3 ) );
		for ( let i = 0; i < nAvailableIndices; i++ )
		{
			this.m_rAvailableEmbeddedIndicesQueue.push( i );
		}

		actualDocument.addEventListener( "mousemove", this.onMouseMove );

		this.m_Observer = new MutationObserver( this.onMutation );
		this.m_Observer.observe( this.m_DomRef.current, {
			// Listen to changes to specific attributes
			attributes: true,

			// Which attributes to listen to changes on.
			// SG nodes increment their sg-generation to force a UV update.
			attributeFilter: ['sg-forced-update-number', 'overlay-key'],

			// Listen to nodes being added/removed
			childList: true,

			// Observe recursively.
			subtree: true,
		} );

		this.createEmbeddedData();

		// A neat little trick to be notified when the console (devtools) is opened.
		// https://stackoverflow.com/a/30638226
		// let regexDevTools = /./;
		// ( regexDevTools as any ).toString = () =>
		// {
		// 	// When DevTools is open, set an interval to constantly update the scene graph in case
		// 	// the developer hand-modifies scene graph DOM attributes.
		// 	const nUpdateIntervalMS = 300;
		// 	console.info( "Detected DevTools. Updating scene graph every " + nUpdateIntervalMS + " ms." );
		// 	setInterval( this.forceLayoutUpdate, nUpdateIntervalMS );
		// };
		// console.log( "Checking for DevTools... %c", regexDevTools );

		// Re-check UVs after all hard-coded page resources loaded. This primarily concerns fonts,
		// which affect panel sizing.
		actualWindow.addEventListener( 'load', this.forceLayoutUpdate );

		// Don't load the rest of the app until all fonts are loaded to prevent font popping.
		// Having to skip around type checking here since Typescript doesn't seem to know about it (Chrome 35+).
		( actualDocument as any ).fonts.ready.then( () => this.setState( { bFontsLoaded: true } ) );

		( actualWindow as any ).forceLayoutUpdate = this.forceLayoutUpdate;
		( actualWindow as any ).toggleDebugPointer = this.toggleDebugPointer;

		if ( this.props.owning_overlay_key || this.props.web_secret )
		{
			SetOwningOverlayKey( this.props.owning_overlay_key, this.m_DomRef.current, this.props.web_secret );
		}
	}

	public componentWillUnmount()
	{
		let actualDocument = this.m_DomRef.current.ownerDocument;
		actualDocument.body.classList.remove( 'SGApp' );
		document.documentElement.classList.remove( 'VROverlay' );

		if ( this.m_Observer )
		{
			this.m_Observer.disconnect();
		}

		actualDocument.removeEventListener( "mousemove", this.onMouseMove );
	}

	public render()
	{
		let rClasses: string[] = [];
		if ( this.state.bShowDebugPointer )
		{
			rClasses.push( "ShowDebugPointer" );
		}
		return (
			<vsg-app class={ rClasses.join( " " ) } ref={ this.m_DomRef } sg-forced-update-number={ this.state.nForcedUpdateNumber }>
				<div className="DebugPointer" ref={ this.m_DebugPointerRef } />
				<canvas
					ref={ this.m_CanvasRef }
					className="EmbeddedData"
					height={ SGApp.k_EmbeddedDataRows } />
				<div className="AppSceneGraph" ref={ this.m_scalingDomRef } >
					{ this.state.bFontsLoaded && this.props.children }
				</div>
			</vsg-app> );
	}

	public addEmbeddedPanelUVs( panel: Panel ): number
	{
		if ( this.m_rAvailableEmbeddedIndicesQueue.length === 0 )
		{
			return null;
		}

		this.m_mapPanels.set( panel.getSGID(), panel );
		return this.m_rAvailableEmbeddedIndicesQueue.shift();
	}

	public removeEmbeddedPanelUVs( panel: Panel )
	{
		if ( !this.m_mapPanels.has( panel.getSGID() ) )
		{
			return;
		}

		this.m_mapPanels.delete( panel.getSGID() );
		this.m_rAvailableEmbeddedIndicesQueue.push( panel.getEmbeddedIndex() );
		this.m_rEmbeddedIndicesToClear.push( panel.getEmbeddedIndex() );
	}

	@bind public onMutation( mutations: MutationRecord[], observer: MutationObserver )
	{
		this.updateAllPanelBounds();
	}

	private createEmbeddedData()
	{
		this.m_CanvasRef.current.width = this.m_nEmbeddedDataWidth;
		this.m_CanvasContext = this.m_CanvasRef.current.getContext( '2d' );
		this.m_CanvasContext.globalCompositeOperation = "copy";
		this.m_CanvasContext.imageSmoothingEnabled = false;
		if ( this.m_Pixels === null )
		{
			this.m_Pixels = this.m_CanvasContext.createImageData( this.m_nEmbeddedDataWidth, SGApp.k_EmbeddedDataRows );
		}

		this.updateAllPanelBounds();
	}

	private updateAllPanelBounds()
	{
		let actualWindow = this.m_DomRef.current.ownerDocument.defaultView;

		let unscaledRect = this.m_scalingDomRef.current.getBoundingClientRect();
		let scaledRect = { x: unscaledRect.width / this.m_fCurrentScale, y: unscaledRect.height / this.m_fCurrentScale };
		let windowRect = { x: actualWindow.innerWidth, y: actualWindow.innerHeight };
		let requiredScale = { x: windowRect.x / scaledRect.x, y: windowRect.y / scaledRect.y };
		let fNewScale = Math.min( requiredScale.x, requiredScale.y, 1.0 );

		if ( fNewScale != this.m_fCurrentScale )
		{
			if ( fNewScale != 1.0 )
			{
				this.m_scalingDomRef.current.setAttribute( 'style', 'transform: scale(' + fNewScale + '); transform-origin: top left' );
			}
			else
			{
				this.m_scalingDomRef.current.removeAttribute( 'style' );
			}

			this.m_fCurrentScale = fNewScale;
			Panel.s_bPanelsAreDirty = true;
		}

		if ( !Panel.s_bPanelsAreDirty )
		{
			return;
		}

		this.m_mapPanels.forEach( ( panel: Panel ) => panel.updateLayoutValues() );

		MarkSceneGraphDirty();
		Panel.s_bPanelsAreDirty = false;

		this.updateEmbeddedData();
	}

	private updateEmbeddedData()
	{
		if ( !this.m_CanvasRef )
		{
			return;
		}

		this.m_nPixelX = 0;

		// Data for the UV slices of each embedded panel are encoded in the row of the page texture.
		// Data can only be included in the RGB components (alpha must be 0xFF) because non-1.0 alpha proportionally
		// scales down the rendered RGB components, losing precision since they're uint8.
		//
		// The top-left pixel of the texture contains a magic value so that the vertex shader can abort reading embedded data
		// if this value is not found. The magic value is "VSG" ( 0x56, 0x53, 0x47, 0xFF ).
		//
		// Starting at the second pixel, an array of 3-pixel groups encodes the UVs for each panel. So a panel with an embedded
		// index of 0 has its UVs encoded in pixels 1, 2, and 3; and a panel with an index of 1 has its UVs in pixels 4, 5, and 6, etc.
		//
		// The UVs are encoded as UV min and max pairs, with each value being a uint16. Mins are inclusive, Maxes are exclusive.
		//
		// Here is a table visualizing the packing structure:
		//
		// +-------+-------------+-------------+-------------+-------------+
		// | PIXEL |     RED     |    GREEN    |     BLUE    |    ALPHA    |
		// +-------+-------------+-------------+-------------+-------------+
		// |   1   |           U Min           |     0x00    |     0xFF    |
		// +-------+---------------------------+-------------+-------------+
		// |   2   |           U Max           | V Min 0..7  |     0xFF    |
		// +-------+---------------------------+-------------+-------------+
		// |   3   |           V Max           | V Min 8..15 |     0xFF    |
		// +-------+---------------------------+-------------+-------------+

		// Magic value ("VSG")
		this.setPixel( 0, 'V'.charCodeAt( 0 ), 'S'.charCodeAt( 0 ), 'G'.charCodeAt( 0 ) );

		// Clear any panels that we were previously embedding but no longer.
		this.m_rEmbeddedIndicesToClear.forEach( ( nEmbeddedIndex: number ) =>
		{
			let nPixelOffset = 1 + ( nEmbeddedIndex * 3 );
			for ( let i = 0; i < 3; i++ )
			{
				this.setPixel( nPixelOffset + i, 0, 0, 0, 0 );
			}
		} );
		this.m_rEmbeddedIndicesToClear = [];

		// Write 3 pixels per panel to encode min/max UVs according to the table above.
		this.m_mapPanels.forEach( ( panel: Panel, nSGID: number ) =>
		{
			let nUMin = panel.m_Rect.x;
			let nUMax = panel.m_Rect.x + panel.m_Rect.width;
			let nVMin = panel.m_Rect.y;
			let nVMax = panel.m_Rect.y + panel.m_Rect.height;
			let nPixelOffset = 1 + ( panel.getEmbeddedIndex() * 3 );
			if ( panel.isExternal() || ( nUMin >= nUMax ) || ( nVMin >= nVMax ) )
			{
				for ( let i = 0; i < 3; i++ )
				{
					this.setPixel( nPixelOffset + 1, 0, 0, 0, 0 );
				}
			}
			else
			{
				this.setPixel(
					nPixelOffset,
					( nUMin & 0x0000FF00 ) >> 8,
					( nUMin & 0x000000FF ),
					0x00,
				);
				this.setPixel(
					nPixelOffset + 1,
					( nUMax & 0x0000FF00 ) >> 8,
					( nUMax & 0x000000FF ),
					( nVMin & 0x000000FF ),
				);
				this.setPixel(
					nPixelOffset + 2,
					( nVMax & 0x0000FF00 ) >> 8,
					( nVMax & 0x000000FF ),
					( nVMin & 0x0000FF00 ) >> 8,
				);
			}
		} );

		let nDirtyWidth = this.m_nDirtyXMax - this.m_nDirtyXMin + 1;
		this.m_CanvasContext.putImageData( this.m_Pixels, 0, 0, this.m_nDirtyXMin, 0, nDirtyWidth, this.m_Pixels.height );

		this.m_nDirtyXMin = -1;
		this.m_nDirtyXMax = -1;
	}

	private setPixel( x: number, red: number, green: number, blue: number, alpha: number = 0xFF )
	{
		this.m_Pixels.data[x * 4 + 0] = red;
		this.m_Pixels.data[x * 4 + 1] = green;
		this.m_Pixels.data[x * 4 + 2] = blue;
		this.m_Pixels.data[x * 4 + 3] = alpha;

		if ( this.m_nDirtyXMin === -1 || x < this.m_nDirtyXMin )
		{
			this.m_nDirtyXMin = x;
		}
		if ( this.m_nDirtyXMax === -1 || x > this.m_nDirtyXMax )
		{
			this.m_nDirtyXMax = x;
		}
	}
}

